import * as THREE from "three/webgpu";
import type { PresentedOrbital } from "../../presentation/mesh";
import type { SpatialProjectionPipeline } from "../spatial/SpatialProjectionPipeline";

const TRACK_SEGMENTS = 128;

export type OrbitalTrackRuntime = {
  geometry: THREE.BufferGeometry;
  line: THREE.LineLoop;
  material: THREE.LineBasicMaterial;
  positions: Float32Array;
  positionAttribute: THREE.BufferAttribute;
  localPoints: THREE.Vector3[];
  authoringPoint: THREE.Vector3;
  semanticPoint: THREE.Vector3;
  visualPoint: THREE.Vector3;
};

export class OrbitalTrackProjector {
  constructor(private readonly spatial: SpatialProjectionPipeline) {}

  create(orbital: PresentedOrbital, color: string) {
    const positions = new Float32Array(TRACK_SEGMENTS * 3);
    const geometry = new THREE.BufferGeometry();
    const positionAttribute = new THREE.BufferAttribute(positions, 3);
    positionAttribute.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute("position", positionAttribute);
    const material = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: orbital.orbit.ringOpacity ?? 0.12,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
    });
    const line = new THREE.LineLoop(geometry, material);
    line.visible = false;
    line.frustumCulled = false;

    const yRadius = orbital.orbit.radius * (1 - Math.min(orbital.orbit.eccentricity, 0.08) * 0.22);
    const quaternion = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(orbital.orbit.tiltX, 0, orbital.orbit.tiltZ),
    );
    const localPoints = Array.from({ length: TRACK_SEGMENTS }, (_, index) => {
      const angle = (index / TRACK_SEGMENTS) * Math.PI * 2;
      return new THREE.Vector3(Math.cos(angle) * orbital.orbit.radius, Math.sin(angle) * yRadius, 0).applyQuaternion(
        quaternion,
      );
    });

    return {
      geometry,
      line,
      material,
      positions,
      positionAttribute,
      localPoints,
      authoringPoint: new THREE.Vector3(),
      semanticPoint: new THREE.Vector3(),
      visualPoint: new THREE.Vector3(),
    } satisfies OrbitalTrackRuntime;
  }

  update(
    runtime: OrbitalTrackRuntime,
    parentAuthoringPosition: THREE.Vector3,
    singularityPosition: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
  ) {
    for (let index = 0; index < runtime.localPoints.length; index += 1) {
      runtime.authoringPoint.copy(runtime.localPoints[index]).add(parentAuthoringPosition);
      this.spatial.toSemantic(runtime.authoringPoint, runtime.semanticPoint, "orbital");
      this.spatial.toVisual(
        runtime.semanticPoint,
        singularityPosition,
        camera,
        viewportWidth,
        viewportHeight,
        runtime.visualPoint,
        "orbital",
      );
      const offset = index * 3;
      runtime.positions[offset] = runtime.visualPoint.x;
      runtime.positions[offset + 1] = runtime.visualPoint.y;
      runtime.positions[offset + 2] = runtime.visualPoint.z;
    }
    runtime.positionAttribute.needsUpdate = true;
  }
}
